import React from 'react';
import { Sparkles, Check, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Container, SectionHeading, Btn, Card, Pill } from '../components/ui';
import Reveal from '../components/Reveal';
import Icon from '../components/Icon';
import ProductPreview from '../components/ProductPreview';
import HeroArchitecture from '../components/HeroArchitecture';
import RotatingWord from '../components/RotatingWord';
import CTASection from '../components/CTASection';
import FaqSearch from '../components/FaqSearch';
import { FeatureCard, SegmentCard, CheckList } from '../components/cards';
import {
  HERO_STATS, HERO_MODES, PILLARS, SOLUTION_LIST, CHANNELS, PAYMENT_METHODS, PAYMENT_FEATURES,
  REPORTING, INTEGRATIONS, COMMODITIES, PARSE_USES,
  tryExtractionUrl, HERO_ROTATORS,
} from '../data/content';
import { IMAGES } from '../data/images';

function SectionAlt({ children, className = '' }) {
  return <section className={`bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24 ${className}`}>{children}</section>;
}
function Section({ children, className = '' }) {
  return <section className={`px-5 py-16 sm:px-8 sm:py-24 ${className}`}>{children}</section>;
}

export default function Home() {
  const { t } = useLanguage();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-50 dark:bg-brand-ink">
        <div className="bp-grid-bg pointer-events-none absolute inset-0 opacity-30 dark:opacity-50" />
        <div className="pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-brand/10 blur-[120px] dark:bg-brand/25" />
        <div className="pointer-events-none absolute right-0 top-1/3 h-96 w-96 rounded-full bg-brand/10 blur-[120px]" />
        <img src={IMAGES.heroAnalytics} alt="" aria-hidden style={{ opacity: 0.08 }} className="pointer-events-none absolute inset-0 hidden h-full w-full object-cover dark:block" />
        <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-r from-brand-ink via-brand-ink/85 to-brand-ink/50 dark:block" />
        <Container className="relative z-10 grid grid-cols-1 items-center gap-12 py-16 sm:py-24 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-6">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm dark:border-white/15 dark:bg-white/5 dark:text-slate-200 dark:shadow-none">
                <Sparkles className="h-3.5 w-3.5 text-brand" /> {t('hero.badge')}
              </span>
            </Reveal>
            <Reveal delay={60}>
              <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.07] text-slate-900 dark:text-white sm:text-5xl md:text-[3.4rem]">
                {t('hero.title')} {t('hero.titleHighlight')}
                <span className="mt-2 block">
                  <RotatingWord words={HERO_ROTATORS} />
                </span>
              </h1>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">{t('hero.subtitle')}</p>
            </Reveal>
            <Reveal delay={180}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Btn href={tryExtractionUrl('hero')} size="lg" arrow data-testid="hero-try-extraction-btn">{t('hero.ctaTry')}</Btn>
                <Btn to="/demo" size="lg" variant="secondary" data-testid="hero-demo-btn">{t('hero.ctaPrimary')}</Btn>
              </div>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
                {HERO_MODES.map((m) => (
                  <span key={m.label} className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                    <Icon name={m.icon} className="h-4 w-4 text-brand" /> {m.label}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={160} className="relative">
              <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-brand/30 to-transparent blur-2xl" />
              <HeroArchitecture className="relative" />
            </Reveal>
          </div>
        </Container>
        <div className="relative border-t border-slate-200 py-5 dark:border-white/10">
          <Container>
            <p className="text-center text-sm text-slate-500 dark:text-slate-400">{t('hero.trustedBy')}</p>
          </Container>
        </div>
      </section>

      {/* STATS */}
      <Section className="!py-12">
        <Container>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {HERO_STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 70}>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 text-center dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <Icon name={s.icon} className="h-5 w-5" />
                  </span>
                  <div className="font-display text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">{s.value}</div>
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* PROBLEM */}
      <SectionAlt>
        <Container>
          <Reveal><SectionHeading eyebrow={t('home.problem.eyebrow')} title={t('home.problem.title')} subtitle={t('home.problem.subtitle')} /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: 'AlertTriangle', title: 'Missing invoices', text: 'Bills slip through the cracks and surface later as back-bills and accruals.' },
              { icon: 'Clock', title: 'Late fees', text: 'High volume plus manual tracking means missed due dates and penalties.' },
              { icon: 'Workflow', title: 'Manual approvals', text: 'Approvals, ACH files and reconciliations consume finance teams.' },
              { icon: 'Database', title: 'Data silos', text: 'AP, procurement and sustainability re-key the same data, again and again.' },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 70}><FeatureCard {...p} /></Reveal>
            ))}
          </div>
        </Container>
      </SectionAlt>

      {/* PLATFORM PILLARS */}
      <Section>
        <Container>
          <Reveal><SectionHeading eyebrow={t('home.platform.eyebrow')} title={t('home.platform.title')} subtitle={t('home.platform.subtitle')} /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.key} delay={i * 60}>
                <div className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-lg dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                      <Icon name={p.icon} className="h-5 w-5" />
                    </span>
                    <span className="font-display text-sm font-bold text-slate-300 dark:text-white/20">{p.step}</span>
                  </div>
                  <h3 className="mt-4 text-base font-bold text-slate-900 dark:text-white">{p.title}</h3>
                  <p className="mt-1 text-sm font-medium text-brand">{p.summary}</p>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{p.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Btn to="/platform" variant="outline" arrow>{t('common.learnMore')}</Btn>
          </div>
        </Container>
      </Section>

      {/* SEGMENTS */}
      <SectionAlt>
        <Container>
          <Reveal><SectionHeading eyebrow={t('home.segments.eyebrow')} title={t('home.segments.title')} subtitle={t('home.segments.subtitle')} /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {SOLUTION_LIST.map((seg, i) => (
              <Reveal key={seg.slug} delay={i * 80}><SegmentCard seg={seg} ctaLabel={t('common.explore')} /></Reveal>
            ))}
          </div>
        </Container>
      </SectionAlt>

      {/* CHANNELS */}
      <Section>
        <Container>
          <Reveal><SectionHeading eyebrow={t('home.channels.eyebrow')} title={t('home.channels.title')} subtitle={t('home.channels.subtitle')} /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CHANNELS.map((c, i) => (
              <Reveal key={c.title} delay={i * 60}><FeatureCard {...c} /></Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* AI PARSING (preview left, text right) */}
      <SectionAlt>
        <Container>
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <Reveal className="order-2 lg:order-1">
              <ProductPreview variant="parsing" title="parse.billportal.io" />
            </Reveal>
            <Reveal delay={80} className="order-1 lg:order-2">
              <SectionHeading
                align="left"
                eyebrow="AI parsing & validation"
                title="Accurate data capture across every commodity"
                subtitle="AI Agents read every bill with high accuracy — capturing charges, usage and meter reads across electricity, water, waste, gas, heating oil, telco and fuels, validated against the figures on the page."
              />
              <div className="mt-6 flex flex-wrap gap-2">
                {COMMODITIES.map((c) => (
                  <Pill key={c.name}><Icon name={c.icon} className="h-3.5 w-3.5 text-brand" /> {c.name}</Pill>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {PARSE_USES.map((u) => (
                  <div key={u.title} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                    <div className="flex items-center gap-2">
                      <Icon name={u.icon} className="h-4 w-4 text-brand" />
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">{u.title}</span>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{u.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-7">
                <Btn href={tryExtractionUrl('parsing_section')} size="lg" arrow data-testid="parsing-try-extraction-btn">{t('hero.ctaTryParser')}</Btn>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Upload a real bill and see the extracted data — no account needed to start.</p>
              </div>
            </Reveal>
          </div>
        </Container>
      </SectionAlt>

      {/* PAYMENTS */}
      <Section>
        <Container>
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <SectionHeading align="left" eyebrow={t('home.payments.eyebrow')} title={t('home.payments.title')} subtitle={t('home.payments.subtitle')} />
              <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {PAYMENT_FEATURES.map((f) => (
                  <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                    <div className="flex items-center gap-2">
                      <Icon name={f.icon} className="h-4.5 w-4.5 text-brand" />
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">{f.title}</span>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{f.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {PAYMENT_METHODS.map((m) => (
                  <Pill key={m.title}><Icon name={m.icon} className="h-3.5 w-3.5 text-brand" /> {m.title}</Pill>
                ))}
              </div>
            </Reveal>
            <Reveal delay={80}>
              <ProductPreview variant="payments" title="pay.billportal.io" />
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* REPORTING */}
      <SectionAlt>
        <Container>
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <Reveal className="order-2 lg:order-1">
              <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10">
                <img src={IMAGES.solar} alt="Renewable energy" className="h-full max-h-96 w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
            <Reveal delay={80} className="order-1 lg:order-2">
              <SectionHeading align="left" eyebrow={t('home.reporting.eyebrow')} title={t('home.reporting.title')} subtitle={t('home.reporting.subtitle')} />
              <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {REPORTING.map((r) => (
                  <div key={r.title} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand"><Icon name={r.icon} className="h-4.5 w-4.5" /></span>
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-white">{r.title}</div>
                      <div className="mt-0.5 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{r.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </Container>
      </SectionAlt>

      {/* INTEGRATIONS */}
      <Section>
        <Container>
          <Reveal><SectionHeading eyebrow={t('home.integrations.eyebrow')} title={t('home.integrations.title')} subtitle={t('home.integrations.subtitle')} /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {INTEGRATIONS.map((it, i) => (
              <Reveal key={it.name} delay={i * 60}>
                <Card className="flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand"><Icon name={it.icon} className="h-5 w-5" /></span>
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">{it.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{it.detail}</div>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <SectionAlt>
        <Container>
          <Reveal><SectionHeading eyebrow="FAQ" title="Frequently asked questions" subtitle="Search our help centre or browse every topic." /></Reveal>
          <Reveal delay={80} className="mt-10"><FaqSearch /></Reveal>
        </Container>
      </SectionAlt>

      <CTASection />
    </div>
  );
}
